import React from 'react';
import { formatTranslation } from 'intl';
import { FiltersTranslationKeys } from 'intl/keys';
import { TranslationKey } from 'intl/types/translationKey';
import {
  GridFilterInputValueProps,
  GridFilterOperator,
} from '@mui/x-data-grid-pro';
import { IntlShape } from 'react-intl';
import { LabelledEntity } from 'shared/types/commonView';
import { LabelledEntityFilterInput } from './labelledEntityFilter';
import {
  createEntityArrayFilterPredicate,
  createEntityValueFilterPredicate,
} from './common';

interface FieldValueItem {
  _id: string;
  label: string;
}

interface FieldValueFilterConfig {
  useItems: () => { items: FieldValueItem[] };
  labelId: TranslationKey;
}

export function createFieldValueFilterInput(
  config: FieldValueFilterConfig
): React.FC<GridFilterInputValueProps> {
  const FilterInputComponent: React.FC<GridFilterInputValueProps> = (props) => {
    const { items } = config.useItems();
    const available: LabelledEntity[] = items.map((item) => ({
      _id: item._id,
      label: item.label,
    }));

    return (
      <LabelledEntityFilterInput
        {...props}
        available={available}
        labelId={config.labelId}
      />
    );
  };

  return FilterInputComponent;
}

export function createFieldValueOperator(
  config: FieldValueFilterConfig
): (intl: IntlShape) => GridFilterOperator {
  const FilterInput = createFieldValueFilterInput(config);

  return (intl: IntlShape): GridFilterOperator => ({
    label: formatTranslation(intl, FiltersTranslationKeys.operatorContains),
    value: 'contains',
    getApplyFilterFn: (filterItem) =>
      createEntityArrayFilterPredicate<LabelledEntity>(filterItem),
    InputComponent: FilterInput,
  });
}

/** For columns where the cell value is a single entity (e.g. proposedCorrectiveAction, impact). */
export function createDirectFieldValueOperator(
  config: FieldValueFilterConfig & {
    getValueFromRow: (row: Record<string, unknown>) => LabelledEntity | null;
  }
): (intl: IntlShape) => GridFilterOperator {
  const FilterInput = createFieldValueFilterInput(config);

  return (intl: IntlShape): GridFilterOperator => ({
    label: formatTranslation(intl, FiltersTranslationKeys.operatorContains),
    value: 'contains',
    getApplyFilterFn: (filterItem) => {
      const fn = createEntityValueFilterPredicate<LabelledEntity>(filterItem);
      if (!fn) return null;
      return (_value: unknown, row: Record<string, unknown>) =>
        fn(config.getValueFromRow(row));
    },
    InputComponent: FilterInput,
  });
}
